/**
 * AnalyticsSearchTypeahead — the Analytics toolbar's search box, and what it actually does.
 *
 * ## The defect this replaces
 *
 * The toolbar used to render `ActionLine`'s generic `Search` field bound to
 * `const [search, setSearch] = useState('')` — local state that no query ever read (flagged
 * in `AnalyticsFiltersHeader` at the old `search`/`onSearchChange` wiring). Worse, the NFC
 * scan button beside it (`showScan`, `scanEntity="certificate"`) dumped the scanned TAG id
 * straight into that dead box, via `ActionLine`'s own built-in effect
 * (`onSearchChange?.(latestScan.tagId)`) — so scanning a tag on this page looked like it
 * should narrow the page and silently did nothing.
 *
 * ## What this is INSTEAD — a fast path into the filters that already exist
 *
 * This page's five narrowing dimensions (Owner / Product / Certificate / Status / Location)
 * are ALREADY wired end-to-end through `state.analytics.filters` — every stat box, the time
 * chart and the scans map all constrain on them (see `AnalyticsFiltersHeader`). Search is not
 * a sixth, independent dimension; it is a TYPEAHEAD that stages into the SAME three entity
 * filters (`ownerIds` / `productIds` / `certificateIds`) the drawer's Owner / Product /
 * Certificate fields already write. Picking a result here produces the exact same applied
 * filter — same chip, same clear behaviour, same query — as ticking it in the drawer.
 *
 * ## Reuse, not invention (§28 / §36 / §47)
 *
 * - **Matching**: the same `searchParams.fields` (dot-paths supported) each drawer field
 *   already searches with, read via the SAME `getValuesByKeys` utility `SearchSelect` uses —
 *   the caller (`AnalyticsFiltersHeader`) derives the groups straight off its own `columns`
 *   config, so there is one field list per entity, not two.
 * - **Rows**: the real `OwnerPreview` / `ProductPreview` / `CertificatePreview` components —
 *   the identical rows the filter drawer's `SearchSelect` renders for these three dimensions.
 *   No bare-MUI list row.
 * - **Panel chrome**: `filterDropdownPaperSx`, the same paper the filter drawer's own
 *   `SearchFilter` popover uses (§69 — a floating surface must not hand-roll its background).
 * - **Group headers**: the same visual treatment `SelectField`'s grouped menu already uses
 *   (11px, 600 weight, 0.06em letter-spacing, uppercase, `text.secondary`).
 *
 * ## The NFC scan button, fixed
 *
 * The scan control beside this field aims at a target whose `entity` is `'certificate'` —
 * exactly what the drawer's own Certificate field's scan button resolves to (see
 * `SearchFilter`'s `SCAN_ENTITY_BY_FIELD`). A scanned tag is resolved server-side
 * (`scanResolveApi.resolveByTags` → `projectResolution`) to the certificate it belongs to,
 * and the resolved certificate id — NOT the raw tag id — is what gets staged into
 * `certificateIds`. A tag with no certificate in this company, or a certificate whose
 * resolution otherwise comes back empty, hits the SAME "not found" toast every other scan
 * target on the platform already shows (`scanMissMessage`, fired from
 * `ScanRoutingContext.land`) — nothing bespoke needed here, because this uses the same
 * `ScanTargetButton` + resolution pipeline as every other scan-enabled field.
 *
 * Deliberately a DEDICATED target key (`SCAN_TARGET_KEY` below), not `SEARCH_SCAN_TARGET_KEY`
 * — `ActionLine`'s own toolbar-search scan effect reads that key UNCONDITIONALLY (it is not
 * gated on `showScan`), so sharing it here would race this component's own resolution against
 * ActionLine's raw-tagId-into-search behaviour. `ActionLine` renders with `showSearch={false}`
 * and `showScan={false}` in `AnalyticsFiltersHeader` for exactly this reason — this component
 * replaces its search slot entirely rather than layering on top of it.
 *
 * NOT registered via `scanning.registerField` — unlike a drawer field (where the ticked
 * checkbox IS the visible report, per `ScanRoutingContext.land`'s "only speak when the
 * operator cannot see it happen"), a scan landing here has no on-screen checkbox to watch: the
 * applied-filter chip appears in `AnalyticsAppliedFilters`, below the sticky toolbar band,
 * which is easy to miss. So every successful scan toasts, same as it would with the drawer
 * closed.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react'

import { List, ListItemButton, ListSubheader, Popover, Stack, Typography } from '@mui/material'

import { CertificatePreview, OwnerPreview, ProductPreview, Search } from 'components'
import { getValuesByKeys } from 'utils'
import { useScanRouting } from 'contexts'
// Direct file imports, not the `components` barrel — mirrors `SearchFilter` / `ActionLine`,
// which both import `ScanTargetButton` this way to avoid a circular import (this module sits
// outside that barrel's own subtree, but the same convention keeps every scan-button call site
// consistent about how it gets there).
import { ScanTargetButton } from 'components/input/ScanTargetButton'
import { filterDropdownPaperSx } from 'components/input/filters/dropdownSurface'

/** Which redux array a group's picks are staged into — see `AnalyticsFiltersHeader`'s adapter. */
export type AnalyticsSearchFilterField = 'ownerIds' | 'productIds' | 'certificateIds'

export interface AnalyticsSearchGroup {
  field: AnalyticsSearchFilterField
  /** Group header — "Owners" / "Products" / "Certificates". */
  label: string
  /** The full entity list this group searches — the SAME arrays the drawer's fields read. */
  options: any[]
  /** The SAME field list + preview key the drawer's `SearchFilter` for this dimension uses. */
  searchParams: { fields: string[]; preview: 'owners' | 'products' | 'certificates' }
}

interface AnalyticsSearchTypeaheadProps {
  groups: AnalyticsSearchGroup[]
  /** Stage `id` into `field`'s applied filter. No-ops if it is already there. */
  onSelect: (field: AnalyticsSearchFilterField, id: string) => void
}

/** Rows shown per group before the rest are left for a real search to narrow down. */
const MAX_RESULTS_PER_GROUP = 5

/**
 * Dedicated scan target — NOT the shared `SEARCH_SCAN_TARGET_KEY`. See the module docblock
 * for why sharing it would race this component's resolution against `ActionLine`'s own
 * built-in (and here unwanted) toolbar-search scan effect.
 */
const SCAN_TARGET_KEY = 'analytics-search-certificate'

/** Case-insensitive substring match over a row's declared searchable fields (dot-paths ok). */
const rowMatches = (row: any, fields: string[], query: string): boolean => {
  try {
    const values = getValuesByKeys(row, fields)
    return JSON.stringify(values).toLowerCase().includes(query)
  } catch {
    return false
  }
}

export const AnalyticsSearchTypeahead = ({ groups, onSelect }: AnalyticsSearchTypeaheadProps) => {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement | null>(null)

  const trimmed = query.trim().toLowerCase()

  const matchedGroups = useMemo(() => {
    if (!trimmed) return []
    return groups
      .map(group => ({
        ...group,
        rows: (group.options ?? [])
          .filter(row => row && rowMatches(row, group.searchParams.fields, trimmed))
          .slice(0, MAX_RESULTS_PER_GROUP),
      }))
      .filter(group => group.rows.length > 0)
  }, [groups, trimmed])

  const hasResults = matchedGroups.length > 0

  const handleQueryChange = (value: string) => {
    setQuery(value)
    setOpen(value.trim().length > 0)
  }

  const handleClose = () => setOpen(false)

  const handleSelect = (field: AnalyticsSearchFilterField, id: string) => {
    onSelect(field, id)
    setQuery('')
    setOpen(false)
  }

  /**
   * Escape closes the dropdown.
   *
   * MUI's own handling never sees it: the Popover below sets `disableAutoFocus` /
   * `disableEnforceFocus` so the field stays typeable while it's open, which means focus never
   * moves into the popover and its Modal-level keydown listener never fires. Mirrors
   * `SearchFilter`'s identical fix for the identical setup.
   */
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      handleClose()
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [open])

  // ── THE SCAN PATH — resolve to a certificate id, then apply it exactly like a click. ──────
  const scanning = useScanRouting()
  const scannedRows = scanning.scannedFor(SCAN_TARGET_KEY)
  const scannedIds = scannedRows.map(row => row.id).join(',')
  // What has already been staged, so a re-render over the same scanned set (e.g. a second
  // field also touching `scanned` state) never re-applies an id that is already in the filter.
  const appliedScanIdsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!scannedRows.length) return
    const fresh = scannedRows.filter(row => !appliedScanIdsRef.current.has(row.id))
    if (!fresh.length) return
    fresh.forEach(row => {
      appliedScanIdsRef.current.add(row.id)
      onSelect('certificateIds', row.id)
    })
    // Cleared, not retained: a scan that lands here is a one-shot "add to the filter" — there
    // is no checkbox for it to keep matching against, unlike the drawer's own field.
    scanning.clearScanned(SCAN_TARGET_KEY)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannedIds])

  return (
    <>
      {/* Field + scan button, one unit at the house 8px gap — same pairing `ActionLine` and
          `SearchFilter` both use for a field beside its scan target.

          `onFocus` on the wrapping Stack, not on `Search` — `Search`'s own prop surface does
          not forward one to the underlying `Input` (it does not spread unknown props), and
          React's synthetic focus event bubbles (it is implemented on `focusin` since React
          17), so this still fires when the inner `<input>` is focused. Re-opens the dropdown
          on refocus without retyping, e.g. after it was dismissed with Escape. */}
      <Stack
        ref={anchorRef}
        direction="row"
        spacing={2}
        alignItems="center"
        onFocus={() => setOpen(trimmed.length > 0)}
      >
        <Search
          value={query}
          onChange={handleQueryChange}
          debounce={false}
          // Honest about scope (§ toolbar comment above) — this is NOT the global ⌘K sidebar
          // search. It narrows THIS page's owner / product / certificate filters only.
          placeholder="Filter by product, owner or certificate"
          /*
            User-reported: `Search`'s own default `minWidth: 300` (Search.tsx) truncated this
            placeholder to "Filter by product, owner or certifica…". Widened HERE, not in the
            shared component — every other `Search` call site keeps its 300px floor.

            ESTIMATED, not measured in a browser (no browser tooling available in this pass):
            `Filter by product, owner or certificate` (39 chars) at the field's actual 16px
            `FIELD_FONT_SIZE` (`@repo/ui`'s `fieldChrome.ts`) measures ~256px in Arial/Helvetica
            (`ImageFont.getbbox`, Lato itself is not installed in this environment to measure
            against directly) — Lato runs a few percent wider than Arial for typical English
            text, so budgeting ~269px (+5%) for the text itself. Add the field's own chrome,
            all from `Input.tsx`: 12px root padding each side, the 24px leading search icon,
            and its 8px icon→text gap (the clear "×" is `display:none` while the field is
            empty, i.e. exactly the state that shows the full placeholder, so it costs nothing
            here) → 12 + 24 + 8 + 269 + 12 ≈ 325px, rounded up to 340 for font-metric slop.

            EFFECT ON THE TOOLBAR DROP THRESHOLD — reported, not applied: this adds ~40px
            (300 → 340) to the toolbar band's real content width. `AnalyticsFiltersHeader.tsx`
            is owned by a concurrent agent this pass and was mid-edit when checked — the
            `TOOLBAR_DROP_MIN_WIDTH_PX = 1560` constant this was scoped against was not found;
            the file currently has `TOOLBAR_STATUS_MIN_WIDTH_PX = 1660` /
            `TOOLBAR_DATE_MIN_WIDTH_PX = 1440` instead. Whichever constant ends up governing
            this row's drop point should be re-measured (or bumped by ~40px, matching this
            change) so the ~12px slack it was set with isn't eaten by this field alone.
          */
          sx={{ minWidth: 340 }}
        />
        <ScanTargetButton
          target={{
            kind: 'filter-field',
            key: SCAN_TARGET_KEY,
            entity: 'certificate',
            label: 'Certificate',
          }}
          destination="the Certificate filter"
        />
      </Stack>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        disableAutoFocus
        disableEnforceFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: theme =>
              filterDropdownPaperSx(theme, anchorRef.current ? anchorRef.current.clientWidth : undefined),
          },
        }}
      >
        {hasResults ? (
          <List sx={{ width: '100%', maxHeight: '50vh', overflowY: 'auto', overflowX: 'hidden', py: 0 }}>
            {matchedGroups.map(group => [
              <ListSubheader
                key={`group-${group.field}`}
                sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  lineHeight: '32px',
                  px: '12px',
                  backgroundColor: 'background.paper',
                }}
              >
                {group.label}
              </ListSubheader>,
              ...group.rows.map(row => (
                <ListItemButton
                  key={`${group.field}-${row.id}`}
                  dense
                  onClick={() => handleSelect(group.field, row.id)}
                  sx={{
                    borderRadius: 2,
                    minHeight: 48,
                    px: '12px',
                    '&:hover, &.Mui-focusVisible': {
                      backgroundColor: 'rgba(127, 72, 223, 0.12)',
                    },
                  }}
                >
                  {group.searchParams.preview === 'owners' && <OwnerPreview size="medium" owner={row} />}
                  {group.searchParams.preview === 'products' && <ProductPreview size="medium" product={row} />}
                  {group.searchParams.preview === 'certificates' && (
                    <CertificatePreview size="medium" certificate={row} />
                  )}
                </ListItemButton>
              )),
            ])}
          </List>
        ) : (
          // Names the control, matching the house pattern (`AnalyticsCustomize`'s
          // `emptySearch: 'No components match that search.'`).
          <Stack justifyContent="center" sx={{ minHeight: 36, px: '12px', py: '8px' }}>
            <Typography variant="body3" color="text.secondary">
              No products, owners or certificates match that search.
            </Typography>
          </Stack>
        )}
      </Popover>
    </>
  )
}

export default AnalyticsSearchTypeahead
